import { ReactNode, useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { StorageQuotaBanner } from '@easyflow/ui-frontend';
import { BRAND_BASE_DOMAIN, type StorageQuotaSnapshot } from '@easyflow/shared';
import Sidebar from './Sidebar';
import Header from './Header';
import MasterTenantHeader from './MasterTenantHeader';
import MobileSidebar from './MobileSidebar';
import MobileBottomNav, { MOBILE_BOTTOM_NAV_HEIGHT_PX } from './MobileBottomNav';
import EmbedderConfigAlert from './EmbedderConfigAlert';
import SipDialpad from '@/components/SipDialpad';
import { useSipPhone } from '@/contexts/SipPhoneContext';
import { PersonalAssistantAccessProvider } from '@/contexts/PersonalAssistantAccessContext';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

interface LayoutProps {
  children: ReactNode;
  variant?: 'tenant' | 'superadmin';
}

export default function Layout(props: LayoutProps) {
  if (props.variant === 'superadmin') return <LayoutContent {...props} />;
  return <PersonalAssistantAccessProvider><LayoutContent {...props} /></PersonalAssistantAccessProvider>;
}

function LayoutContent({ children, variant = 'tenant' }: LayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const sipPhone = useSipPhone();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const lastModuleToast = useRef(0);

  useEffect(() => {
    const handleSidebarStateChange = (event: CustomEvent) => {
      setSidebarCollapsed(event.detail.collapsed);
    };

    const handleModuleDisabled = (event: CustomEvent) => {
      const now = Date.now();
      if (now - lastModuleToast.current < 3000) return;
      lastModuleToast.current = now;
      toast({
        title: 'Modulo non disponibile',
        description: event.detail?.message || 'Questo modulo non è abilitato per il tuo tenant.',
        variant: 'destructive',
      });
    };

    window.addEventListener('sidebar-state-change', handleSidebarStateChange as EventListener);
    window.addEventListener('api:module-disabled', handleModuleDisabled as EventListener);
    return () => {
      window.removeEventListener('sidebar-state-change', handleSidebarStateChange as EventListener);
      window.removeEventListener('api:module-disabled', handleModuleDisabled as EventListener);
    };
  }, [toast]);

  if (variant === 'superadmin') {
    return (
      <div className="h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100 dark:from-slate-950 dark:via-blue-950/30 dark:to-slate-900 overflow-hidden">
        <div className="flex flex-col h-full">
          <MasterTenantHeader />
          <main className="flex-1 p-3 sm:p-4 md:p-6 overflow-y-auto overflow-x-hidden">
            {children}
          </main>
        </div>
        
        {/* Global SIP Phone - Also available in superadmin */}
        <SipDialpad
          isOpen={sipPhone.isOpen}
          onClose={sipPhone.closePhone}
        />
      </div>
    );
  }

  // Padding-bottom mobile: altezza nominale barra (64px) + safe-area iOS.
  // Su desktop (md+) la barra è nascosta e ripristiniamo il padding standard.
  // Nota: la classe DEVE essere statica perché Tailwind JIT estrae solo
  // class names visibili a tempo di build.
  const mobileBottomPadClass = 'pb-[calc(64px+env(safe-area-inset-bottom))] md:pb-6';

  return (
    <div className="h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100 dark:from-slate-950 dark:via-blue-950/30 dark:to-slate-900 overflow-hidden">
      <MobileSidebar isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      <div className="flex h-full">
        <Sidebar />
        <div
          className={cn(
            "flex-1 flex flex-col h-full overflow-hidden transition-all duration-300 ease-smooth",
            "min-w-0 w-full",
            "ml-0 md:ml-16",
            !sidebarCollapsed && "md:ml-64"
          )}
        >
          <Header />
          {/* Task #1237 — Banner sticky quota storage (R2/DB) quando critical/blocked */}
          <StorageQuotaBanner
            fetchSnapshot={async () => {
              const r = await apiClient.get<StorageQuotaSnapshot>('/storage/quota/me');
              return r.data ?? null;
            }}
            onNavigateDetail={() => navigate(`/${tenantSlug}/settings?tab=storage`)}
            supportContactHref={`mailto:supporto@${BRAND_BASE_DOMAIN}?subject=Quota%20storage%20esaurita`}
          />
          <EmbedderConfigAlert />
          <main
            className={cn(
              "flex-1 min-h-0 p-3 sm:p-4 md:p-6 overflow-y-auto overflow-x-hidden",
              mobileBottomPadClass,
            )}
          >
            {children}
          </main>
        </div>
      </div>

      {/* Bottom nav mobile: visibile solo <md, replica il pattern WhatsApp/Slack/
          Outlook con 4 shortcut configurabili + slot fisso "Altro" che apre il
          drawer hamburger esistente. */}
      <MobileBottomNav onOpenMore={() => setMobileMenuOpen(true)} />

      {/* Global SIP Phone - Persistent across all pages */}
      <SipDialpad
        isOpen={sipPhone.isOpen}
        onClose={sipPhone.closePhone}
      />
    </div>
  );
}
